import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import { padZeros, RoleContext } from '@/app/api/common';
import { checkNoError, copyChannelInfo, ChannelInfo, getChannelByEpgId, getUrl, getField, UrlInfo } from '@/features/channels/channelsSlice';
import { HotelInfoPublic } from '@/features/projects/projectsSlice';

import { usePageSubscription } from '@/app/websocket/hooks';
import { NumberText } from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import { DeleteModal, RightBar } from '@/components/Common/Modals';
import MenuItem from '@/components/Common/MenuItem';

import { ChannelListsProps } from '@/pages/ChannelLists/ChannelLists';
import Channel from './Channel';

/*
export interface ChannelRow {
  epg_id:				string;
  name:					string;
  real_epg_id:			string;
  languages:			string[];
  urls:					string;
}*/

const Channels = (props: ChannelListsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const globalChannels = useAppSelector((state) => state.channels);
  const hotelChannels = useAppSelector((state) => state.hotelChannels);
  const logins = useAppSelector((state) => state.logins);
  
  const storeChannels =	props.project
					?	hotelChannels
					:	globalChannels;

  const channels = props.hideContainer && props.externalChannels
    ? { channels: props.externalChannels }
    : storeChannels;

  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as ChannelInfo[]);
  
  const [toDelete, setToDelete] = React.useState("");
  const { epg_id, tab } = useParams();
  const role = React.useContext(RoleContext);

  // Subscribe to channel change notifications
  usePageSubscription(
    props.project
      ? [{ type: 'channel', scope_id: props.project.id }]
      : [{ type: 'channel' }]
  );

  React.useEffect(() => {
    if (	props.project &&
    		props.setlocalChannel &&
       		(	!hotelChannels.channels ||
       			hotelChannels.channels.length == 0
       		) &&
       		(role.admin || role?.privilages?.includes("ViewChannels") || role?.privilages?.includes("ChangeChannels"))
       )
      store.getChannels(props.project.id);
  }, []);
  
  const handleRequestDelete = React.useCallback((epg_id: string) => {
    console.log("Channels/handleRequestDelete/epg_id=" + epg_id);
    setToDelete(epg_id);
  }, [setToDelete]);
  
  const handleDelete = React.useCallback(async (evt: Object, answer: string) => {
    if (	!toDelete ||
        	!channels
       )
      return;
    
    const epg_id = toDelete;
    setToDelete("");
    
    if (answer !== "yes") {
      return;
    }
    
    const chan = getChannelByEpgId(epg_id, channels);
    if (!chan)
      return;
    
    let newChan = copyChannelInfo(chan);
    newChan.to_delete = true;
    
    const mess = await store.setChannel(
      newChan,
      props.project
      ?	props.project.id
      : ''
    );
    
    console.log("Channels/handleDelete/mess=", mess);
    
    if (!checkNoError(mess))
      return false;

    if (props.project) {
      store.getProjects(props.project.id);
      return true;
    }
    
    store.getChannels();
  }, [channels, toDelete]);
  
  React.useEffect(() => {
    setCols([
    {
        field:			"epg_id",
        headerName:		t("channels:grid:epg_id"),
        sort:			"asc",
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    {
        field:			"name",
        headerName:		t("channels:grid:name"),
        flex:			1,
        minWidth:		200,
        //maxWidth:		300,
    },
    {
        field:			"real_epg_id",
        headerName:		t("channels:grid:real_epg_id"),
        flex:			1,
        minWidth:		150,
        maxWidth:		250,
    },
    {
        field:			"logo",
        headerName:		t("channels:grid:logo"),
        flex:			2,
        minWidth:		200,
    },
    {
        field:			"languages",
        headerName:		t("channels:grid:languages"),
        flex:			0,
        minWidth:		80,
        maxWidth:		120,
    },
    /*{
        field:			"urls",
        cellRenderer:	UrlsCell,
        headerName:		t("channels:grid:urls"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        tooltipValueGetter:		null,
    },*/
    ...(!props.hideContainer && (role?.admin || role?.privilages?.includes("ChangeChannels"))
    ? [{
        field:			"options",
        headerName:		t("channels:grid:options"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] : []),
    ]);
    
    const rws: ChannelInfo[] = [];
    for (let channel of channels.channels) {
      let row = copyChannelInfo(channel);
      
      let rowA = row as any;
      rowA.requestDelete = handleRequestDelete;
      
      if (props.project) {
        rowA.project = props.project;
      }
      
      rws.push(row);
      /*const row: ChannelRow = {
      	epg_id:				channel.epg_id,
		name:				channel.name,
		real_epg_id:		channel.real_epg_id,
		logo:				channel.logo,
		languages:			channel.languages,
		urls:				channel.urls,
		contact:			getField(proj.urls, "contact"),
      };*/
    }
    
    setRows(rws);
  }, [channels.channels, t]);
  
  const handleCellClick = React.useCallback((evt: any) => {
    if (	evt.colDef.field == "options" ||
        	evt.colDef.field == "urls"
       )
      return;
    if(!role.admin && !(role?.privilages?.includes("ChangeChannels") || role?.privilages?.includes("ViewChannels")))
      return;
    navigate(getChannelPageUrl(evt.data.epg_id, evt.data.project));
  }, [navigate, role]);
  
  const updateIsOpen = React.useCallback((val: boolean) => {
    if (val)
      return;
    
    navigate(getChannelPageUrl("", props.project));
  }, [props.project]);
  console.log("rows",rows)
  // const isAdmin =	logins.login &&
  // 					logins.login.role == 'admin';
  
  // if (!isAdmin)
  //   return null;
  
  return (
  <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               props.project ? '' : 'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
    {!props.project &&
      <div className="text-[32px]/[48px]">
        { t('channels:caption') }
      </div>
    }
      <div>
        <NumberText>
          { channels.channels.length }
        </NumberText>
        { ' ' +
          t('channels:channels')
        }
      </div>
    <Divider className="my-[8px]" />
    </FlexRow> 
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               props.project ? "" : "px-[32px]"
             ]}>
    { role.admin && !props.hideContainer &&
      <Button className="uppercase bg-[#1B69AA] font-bold rounded-[5px] px-2 mb-[16px]"
              onClick={() => navigate(getChannelPageUrl('__new__', props.project))}>
        <FlexRow justify="center">
          <div className="material-icons !text-white">
            add
          </div>
          <div className="m14 text-white leading-normal">
            { t('channels:new') }
          </div>
        </FlexRow>
      </Button>
    }
    <Grid rowData={rows}
          columnDefs={cols}
          filter={true}
          globalFilter={true}
          showPages={true}
          maxHeight={(props.project || props.hideContainer) ? undefined : "calc(100vh - 340px)"}
          onCellClicked={handleCellClick}
          pageSize={10} />
    {tab === "clone" &&
	<RightBar onOpenChange={updateIsOpen}>
	  <Channel />
	</RightBar>
    }
    {toDelete != '' &&
	  <DeleteModal title={t('channels:channel:delete:caption')}
	               message={t('channels:channel:delete:message_n', {n: toDelete})}
	               onResult={handleDelete}>
        <>
        </>
	  </DeleteModal>
	}
  </FlexRow>
  </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = React.useContext(RoleContext);

  const [open, setOpen] = React.useState(false);
  
  const handleClone = () => {
    setOpen(false);
    
    navigate(getChannelPageUrl(props.data.epg_id, props.data.project, "clone"))
  };
  
  const handleDelete = () => {
    setOpen(false);
    props.data.requestDelete(props.data.epg_id);
  };
  
  return (
  <Popover key={1}
           isOpen={open}
           onOpenChange={setOpen}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {
          (role?.admin || role?.privilages?.includes("ChangeChannels") || role?.privilages?.includes("ViewChannels"))&&
        <MenuItem icon="edit_note"
                  label={t('channels:edit_channel')}
                  onClick={() => navigate(getChannelPageUrl(props.data.epg_id, props.data.project))} />
        }
        {
          role?.admin &&
          <MenuItem icon="content_copy"
                    label={t('channels:clone_channel')}
                    onClick={handleClone} />
          }
          {/*
          <MenuItem icon="public_off"
                    label={t('channels:deactivate_channel')} />
          */}
          {
          (role?.admin || role?.privilages?.includes("DeleteChannel")) &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('channels:delete_channel')}
                    onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

const UrlsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [open, setOpen] = React.useState(false);
  
  return (
  <FlexRow justify="center">
  {props.data && props.data.urls && props.data.urls.length &&
  <Popover key={1}
           placement="bottom-end"
           onOpenChange={setOpen}
           radius="md"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="h-full w-full text-center">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          { open ? 'expand_less' : 'navigate_next' }
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] r14">
        <FlexRow align="start">
          <FlexRow direction="column"
                   flex={1}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('channels:grid:type') }
            </FlexRow>
          {props.data.urls.map((url: UrlInfo, i: number) =>
            <FlexRow key={i}
                      className="p-[10px]">
              { url.type }
            </FlexRow>
          )}
          </FlexRow>
          <FlexRow  direction="column"
                    flex={3}>
            <FlexRow className="bg-[--Mid-Grey] p-[10px] b14">
              { t('channels:grid:url') }
            </FlexRow>
          {props.data.urls.map((url: UrlInfo, i: number) =>
            <FlexRow key={i}
                     className="p-[10px]">
              { url.url }
            </FlexRow>
          )}
          </FlexRow>
        </FlexRow>
      </FlexRow>
    </PopoverContent>
  </Popover>
  }
  </FlexRow>
  );
};

export function getChannelPageUrl(epg_id: string, project?: HotelInfoPublic | null, op?: string) {
  let base =	project
	  			?	`/projects/${encodeURIComponent(project.id)}/channels`
	  			:	'/channels';
  
  if (epg_id)
    base += `/${epg_id}`;
  
  if (op)
    base += `/${op}`;
  
  return base;
}


export default Channels;


